import {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Box, Divider, Select, Space, Text, Title } from "@mantine/core";
import { Controller, useForm } from "react-hook-form";
import { ButtonsSelect } from "../../button/ButtonsSelect";
import { useMutation } from "@apollo/client";
import { UPDATE_REMOVABLE_ITEM } from "../../../gql/items";
import { UPDATE_PRODUCT } from "../../../gql/product";

import {
  formatChoicesData,
  filterShadeByShadeType,
} from "../../../utils/item.utils";
import { PartialAndFullDentureProsthesisShadeType } from "../../../types/enums";
import {
  FullDentureTeethShape,
  shadeTypeChoices,
} from "../../../types/constants";
import { Product } from "../../../types/interfaces";

type AestheticsFormData = {
  // single selection (used when not separated)
  shadeType: PartialAndFullDentureProsthesisShadeType;
  teethShadeId: string;
  gingivaShadeId: string;
  teethShape: string;

  // per arcade selections
  maxillaireShadeType?: PartialAndFullDentureProsthesisShadeType;
  maxillaireTeethShadeId?: string;
  maxillaireGingivaShadeId?: string;
  maxillaireTeethShape?: string;
  mandibulaireShadeType?: PartialAndFullDentureProsthesisShadeType;
  mandibulaireTeethShadeId?: string;
  mandibulaireGingivaShadeId?: string;
  mandibulaireTeethShape?: string;
};

type CreateRemovableItemFullDentureAestheticsProps = {
  product: Product;
  removableItemChoicesData: any;
  isConfirmationMode?: boolean;
  previousProduct?: Product;
};

export interface AestheticsRef {
  saveData: () => Promise<void>;
}

export const CreateRemovableItemFullDentureAesthetics = forwardRef<
  AestheticsRef,
  CreateRemovableItemFullDentureAestheticsProps
>(
  (
    {
      product,
      removableItemChoicesData,
      isConfirmationMode = false,
      previousProduct,
    },
    ref
  ) => {
    const newRemovableItem = product.removableItem?.[0];
    const itemPreferences = product.itemPreferences;
    const isBimaxillarySeparated =
      newRemovableItem?.isBimaxillarySeparated ?? false;

    const currentArcadePrefs = itemPreferences?.fullDentureArcadeData;
    const prevRemovableItem = previousProduct?.removableItem?.[0];
    const prevItemPreferences = previousProduct?.itemPreferences;
    const prevArcadePrefs = prevItemPreferences?.fullDentureArcadeData;

    const {
      control,
      watch,
      formState: { errors },
      trigger,
    } = useForm<AestheticsFormData>({
      defaultValues: {
        shadeType:
          (itemPreferences?.shadeType as PartialAndFullDentureProsthesisShadeType) ??
          (prevItemPreferences?.shadeType as PartialAndFullDentureProsthesisShadeType) ??
          PartialAndFullDentureProsthesisShadeType.VITA_CLASSIC,
        teethShadeId:
          newRemovableItem?.teethShade?.id ??
          prevRemovableItem?.teethShade?.id ??
          "A3_VITA_CLASSIC",
        gingivaShadeId:
          newRemovableItem?.gingivaShade?.id ??
          prevRemovableItem?.gingivaShade?.id ??
          "GINGIVA_STANDARD",
        teethShape:
          itemPreferences?.teethShape ??
          prevItemPreferences?.teethShape ??
          "SQUARE",

        // per arcade defaults values
        maxillaireShadeType:
          (currentArcadePrefs?.maxillaire
            ?.shadeType as PartialAndFullDentureProsthesisShadeType) ??
          (prevArcadePrefs?.maxillaire
            ?.shadeType as PartialAndFullDentureProsthesisShadeType) ??
          PartialAndFullDentureProsthesisShadeType.VITA_CLASSIC,
        maxillaireTeethShadeId:
          currentArcadePrefs?.maxillaire?.teethShadeId ??
          prevArcadePrefs?.maxillaire?.teethShadeId ??
          "A3_VITA_CLASSIC",
        maxillaireGingivaShadeId:
          currentArcadePrefs?.maxillaire?.gingivaShadeId ??
          prevArcadePrefs?.maxillaire?.gingivaShadeId ??
          "GINGIVA_STANDARD",
        maxillaireTeethShape:
          currentArcadePrefs?.maxillaire?.teethShape ??
          prevArcadePrefs?.maxillaire?.teethShape ??
          "SQUARE",
        mandibulaireShadeType:
          (currentArcadePrefs?.mandibulaire
            ?.shadeType as PartialAndFullDentureProsthesisShadeType) ??
          (prevArcadePrefs?.mandibulaire
            ?.shadeType as PartialAndFullDentureProsthesisShadeType) ??
          PartialAndFullDentureProsthesisShadeType.VITA_CLASSIC,
        mandibulaireTeethShadeId:
          currentArcadePrefs?.mandibulaire?.teethShadeId ??
          prevArcadePrefs?.mandibulaire?.teethShadeId ??
          "A3_VITA_CLASSIC",
        mandibulaireGingivaShadeId:
          currentArcadePrefs?.mandibulaire?.gingivaShadeId ??
          prevArcadePrefs?.mandibulaire?.gingivaShadeId ??
          "GINGIVA_STANDARD",
        mandibulaireTeethShape:
          currentArcadePrefs?.mandibulaire?.teethShape ??
          prevArcadePrefs?.mandibulaire?.teethShape ??
          "SQUARE",
      },
    });

    const [updateRemovableItem] = useMutation(UPDATE_REMOVABLE_ITEM, {
      refetchQueries: ["getOrderByUniqueAttribute"],
      awaitRefetchQueries: true,
    });

    const [updateProduct] = useMutation(UPDATE_PRODUCT, {
      refetchQueries: ["getOrderByUniqueAttribute"],
      awaitRefetchQueries: true,
    });

    // single mode watches
    const teethShadeId = watch("teethShadeId");
    const gingivaShadeId = watch("gingivaShadeId");
    const teethShape = watch("teethShape");
    const currentShadeType = watch("shadeType");

    // Per-arcade watches
    const watchMaxillaireShadeType = watch("maxillaireShadeType");
    const watchMandibulaireShadeType = watch("mandibulaireShadeType");
    const maxillaireTeethShadeId = watch("maxillaireTeethShadeId");
    const maxillaireGingivaShadeId = watch("maxillaireGingivaShadeId");
    const maxillaireTeethShape = watch("maxillaireTeethShape");
    const mandibulaireTeethShadeId = watch("mandibulaireTeethShadeId");
    const mandibulaireGingivaShadeId = watch("mandibulaireGingivaShadeId");
    const mandibulaireTeethShape = watch("mandibulaireTeethShape");

    const [filteredTeethShades, setFilteredTeethShades] = useState<any[]>([]);
    const [filteredMaxillaireTeethShades, setFilteredMaxillaireTeethShades] =
      useState<any[]>([]);
    const [
      filteredMandibulaireTeethShades,
      setFilteredMandibulaireTeethShades,
    ] = useState<any[]>([]);

    useEffect(() => {
      if (
        !isBimaxillarySeparated &&
        removableItemChoicesData?.getItemShadesWhere
      ) {
        const filtered = filterShadeByShadeType(
          removableItemChoicesData?.getItemShadesWhere,
          currentShadeType
        );
        setFilteredTeethShades(formatChoicesData(filtered));
      }
    }, [currentShadeType, isBimaxillarySeparated, removableItemChoicesData]);

    // Filter shades for per-arcade mode
    useEffect(() => {
      if (
        isBimaxillarySeparated &&
        removableItemChoicesData?.getItemShadesWhere
      ) {
        const maxFiltered = filterShadeByShadeType(
          removableItemChoicesData?.getItemShadesWhere,
          watchMaxillaireShadeType
        );
        setFilteredMaxillaireTeethShades(formatChoicesData(maxFiltered));

        const mandFiltered = filterShadeByShadeType(
          removableItemChoicesData?.getItemShadesWhere,
          watchMandibulaireShadeType
        );
        setFilteredMandibulaireTeethShades(formatChoicesData(mandFiltered));
      }
    }, [
      watchMaxillaireShadeType,
      watchMandibulaireShadeType,
      isBimaxillarySeparated,
      removableItemChoicesData,
    ]);

    const saveData = async (): Promise<void> => {
      const isValidForm = await trigger();
      if (!isValidForm) {
        throw new Error("Formulaire invalide");
      }

      if (!isBimaxillarySeparated) {
        // single mode
        if (product && teethShape && currentShadeType) {
          await updateProduct({
            variables: {
              where: { id: product.id },
              data: {
                itemPreferences: {
                  shadeType: currentShadeType,
                  teethShape: teethShape,
                },
              },
            },
          });
        }

        if (newRemovableItem && teethShadeId) {
          await updateRemovableItem({
            variables: {
              where: { id: newRemovableItem.id },
              data: { teethShade: { connect: { id: teethShadeId } } },
            },
          });
        }

        if (newRemovableItem && gingivaShadeId) {
          await updateRemovableItem({
            variables: {
              where: { id: newRemovableItem.id },
              data: { gingivaShade: { connect: { id: gingivaShadeId } } },
            },
          });
        }
      } else {
        // separated mode
        if (
          product &&
          watchMaxillaireShadeType &&
          maxillaireTeethShadeId &&
          maxillaireGingivaShadeId &&
          maxillaireTeethShape &&
          watchMandibulaireShadeType &&
          mandibulaireTeethShadeId &&
          mandibulaireGingivaShadeId &&
          mandibulaireTeethShape
        ) {
          const currentPreferences = product.itemPreferences || {};
          await updateProduct({
            variables: {
              where: { id: product.id },
              data: {
                itemPreferences: {
                  ...currentPreferences,
                  fullDentureArcadeData: {
                    maxillaire: {
                      ...(currentPreferences.fullDentureArcadeData
                        ?.maxillaire || {}),
                      shadeType: watchMaxillaireShadeType,
                      teethShadeId: maxillaireTeethShadeId,
                      gingivaShadeId: maxillaireGingivaShadeId,
                      teethShape: maxillaireTeethShape,
                    },
                    mandibulaire: {
                      ...(currentPreferences.fullDentureArcadeData
                        ?.mandibulaire || {}),
                      shadeType: watchMandibulaireShadeType,
                      teethShadeId: mandibulaireTeethShadeId,
                      gingivaShadeId: mandibulaireGingivaShadeId,
                      teethShape: mandibulaireTeethShape,
                    },
                  },
                },
              },
            },
          });
        }
      }
    };

    useImperativeHandle(ref, () => ({ saveData }));

    if (!newRemovableItem) {
      return (
        <div>
          <Text>
            {"Erreur sur l'ajout de produit. Veuillez contacter le support."}
          </Text>
        </div>
      );
    }

    const gingivaShadeChoices = formatChoicesData(
      removableItemChoicesData?.getItemShadesWhere?.filter(
        shade => shade.gingiva === true
      ) ?? []
    );

    return (
      <Box>
        {isConfirmationMode && (
          <>
            <Title order={2}>Pouvez-vous confirmer vos choix ?</Title>
            <Space h="xl" />
          </>
        )}
        {!isBimaxillarySeparated ? (
          // single mode
          <>
            <Title order={3}>Esthétique</Title>
            <Space h="xs" />

            <Controller
              name="shadeType"
              rules={{ required: true }}
              control={control}
              render={({ field }) => (
                <ButtonsSelect
                  data={shadeTypeChoices}
                  value={field.value}
                  onChange={value => field.onChange(value)}
                  numberOfColumns={2}
                />
              )}
            />
            <Space h="lg" />

            <Title order={4}>Teinte des dents</Title>
            <Space h="xs" />

            <Controller
              name="teethShadeId"
              rules={{ required: true }}
              control={control}
              render={({ field }) => (
                <Select
                  placeholder="Sélectionner une teinte"
                  data={filteredTeethShades}
                  value={field.value}
                  onChange={value => field.onChange(value ?? "")}
                  searchable
                  allowDeselect={false}
                />
              )}
            />

            {errors?.teethShadeId?.type === "required" && (
              <Text size="sm" style={{ color: "red" }}>
                Veuillez sélectionner une teinte de dents
              </Text>
            )}

            <Space h="lg" />

            <Title order={4}>Teinte de la gencive</Title>
            <Space h="xs" />
            <Controller
              name="gingivaShadeId"
              rules={{ required: true }}
              control={control}
              render={({ field }) => (
                <ButtonsSelect
                  data={gingivaShadeChoices}
                  value={field.value}
                  onChange={value => field.onChange(value)}
                  numberOfColumns={2}
                />
              )}
            />

            {errors?.gingivaShadeId?.type === "required" && (
              <Text size="sm" style={{ color: "red" }}>
                Veuillez sélectionner une teinte de gencive
              </Text>
            )}

            <Space h="lg" />
            <Title order={4}> Forme des dents</Title>
            <Space h="xs" />
            <Controller
              name="teethShape"
              rules={{ required: true }}
              control={control}
              render={({ field }) => (
                <ButtonsSelect
                  data={FullDentureTeethShape}
                  value={field.value}
                  onChange={value => field.onChange(value)}
                  numberOfColumns={3}
                />
              )}
            />
            {errors?.teethShape?.type === "required" && (
              <Text size="sm" style={{ color: "red" }}>
                Veuillez sélectionner une forme de dents
              </Text>
            )}
          </>
        ) : (
          // per arcade mode
          <>
            {/* maxillaire */}
            <Title order={3}>Arcade Maxillaire</Title>
            <Space h="md" />

            <Title order={4}>Esthétique</Title>
            <Space h="xs" />
            <Controller
              name="maxillaireShadeType"
              rules={{ required: true }}
              control={control}
              render={({ field }) => (
                <ButtonsSelect
                  data={shadeTypeChoices}
                  value={field.value}
                  onChange={value => field.onChange(value)}
                  numberOfColumns={2}
                />
              )}
            />
            <Space h="lg" />

            <Title order={4}>Teinte des dents</Title>
            <Space h="xs" />
            <Controller
              name="maxillaireTeethShadeId"
              rules={{ required: true }}
              control={control}
              render={({ field }) => (
                <Select
                  placeholder="Sélectionner une teinte"
                  data={filteredMaxillaireTeethShades}
                  value={field.value}
                  onChange={value => field.onChange(value ?? "")}
                  searchable
                  allowDeselect={false}
                />
              )}
            />
            {errors?.maxillaireTeethShadeId?.type === "required" && (
              <Text size="sm" style={{ color: "red" }}>
                Veuillez sélectionner une teinte de dents
              </Text>
            )}
            <Space h="lg" />

            <Title order={4}>Teinte de la gencive</Title>
            <Space h="xs" />
            <Controller
              name="maxillaireGingivaShadeId"
              rules={{ required: true }}
              control={control}
              render={({ field }) => (
                <ButtonsSelect
                  data={gingivaShadeChoices}
                  value={field.value}
                  onChange={value => field.onChange(value)}
                  numberOfColumns={2}
                />
              )}
            />
            {errors?.maxillaireGingivaShadeId?.type === "required" && (
              <Text size="sm" style={{ color: "red" }}>
                Veuillez sélectionner une teinte de gencive
              </Text>
            )}
            <Space h="lg" />

            <Title order={4}>Forme des dents</Title>
            <Space h="xs" />
            <Controller
              name="maxillaireTeethShape"
              rules={{ required: true }}
              control={control}
              render={({ field }) => (
                <ButtonsSelect
                  data={FullDentureTeethShape}
                  value={field.value}
                  onChange={value => field.onChange(value)}
                  numberOfColumns={3}
                />
              )}
            />
            {errors?.maxillaireTeethShape?.type === "required" && (
              <Text size="sm" style={{ color: "red" }}>
                Veuillez sélectionner une forme de dents
              </Text>
            )}

            <Space h="xl" />
            <Divider />
            <Space h="xl" />

            {/* mandibulaire */}
            <Title order={3}>Arcade Mandibulaire</Title>
            <Space h="md" />

            <Title order={4}>Esthétique</Title>
            <Space h="xs" />
            <Controller
              name="mandibulaireShadeType"
              rules={{ required: true }}
              control={control}
              render={({ field }) => (
                <ButtonsSelect
                  data={shadeTypeChoices}
                  value={field.value}
                  onChange={value => field.onChange(value)}
                  numberOfColumns={2}
                />
              )}
            />
            <Space h="lg" />

            <Title order={4}>Teinte des dents</Title>
            <Space h="xs" />
            <Controller
              name="mandibulaireTeethShadeId"
              rules={{ required: true }}
              control={control}
              render={({ field }) => (
                <Select
                  placeholder="Sélectionner une teinte"
                  data={filteredMandibulaireTeethShades}
                  value={field.value}
                  onChange={value => field.onChange(value ?? "")}
                  searchable
                  allowDeselect={false}
                />
              )}
            />
            {errors?.mandibulaireTeethShadeId?.type === "required" && (
              <Text size="sm" style={{ color: "red" }}>
                Veuillez sélectionner une teinte de dents
              </Text>
            )}
            <Space h="lg" />

            <Title order={4}>Teinte de la gencive</Title>
            <Space h="xs" />
            <Controller
              name="mandibulaireGingivaShadeId"
              rules={{ required: true }}
              control={control}
              render={({ field }) => (
                <ButtonsSelect
                  data={gingivaShadeChoices}
                  value={field.value}
                  onChange={value => field.onChange(value)}
                  numberOfColumns={2}
                />
              )}
            />
            {errors?.mandibulaireGingivaShadeId?.type === "required" && (
              <Text size="sm" style={{ color: "red" }}>
                Veuillez sélectionner une teinte de gencive
              </Text>
            )}
            <Space h="lg" />

            <Title order={4}>Forme des dents</Title>
            <Space h="xs" />
            <Controller
              name="mandibulaireTeethShape"
              rules={{ required: true }}
              control={control}
              render={({ field }) => (
                <ButtonsSelect
                  data={FullDentureTeethShape}
                  value={field.value}
                  onChange={value => field.onChange(value)}
                  numberOfColumns={3}
                />
              )}
            />
            {errors?.mandibulaireTeethShape?.type === "required" && (
              <Text size="sm" style={{ color: "red" }}>
                Veuillez sélectionner une forme de dents
              </Text>
            )}
          </>
        )}
        <Space h="xl" />
      </Box>
    );
  }
);

CreateRemovableItemFullDentureAesthetics.displayName =
  "CreateRemovableItemFullDentureAesthetics";
